<template>
  <div
    class="project-card"
    :class="{ inactive: isInactive }"
    @click="$emit('transaction-browsing', project)"
  >
    <div class="project-main">
      <div class="project-logo">
        <project-logo :project="project" size="lg" />
      </div>
      <div class="project-name" :title="project.name">{{ project.name }}</div>
      <a :href="project.link" class="project-link" target="_blank" @click.stop>{{ formattedLink }}</a>
      <project-control-icons
        :permission="transactionsBrowsingPermission"
        :pending="isPendingBrowsingPermission"
        @disable="$emit('disable-project', project)"
      />
    </div>
    <div class="project-info">
      <div class="project-info-point">
        <span class="project-info-name">{{ $t('common.balance') }}</span>
        <span class="project-info-price">{{ displayBalance }}</span>
      </div>
      <div class="project-info-point">
        <span class="project-info-name">{{ $t('average.check') }}</span>
        <span class="project-info-price">{{ displayAvgAmount }}</span>
      </div>
      <div class="project-info-point">
        <span class="project-info-name">{{ $t('payments.in.day') }}</span>
        <span class="project-info-price">{{ project.paymentsPerDay }}</span>
      </div>
      <div class="project-info-point">
        <span class="project-info-name">{{ $t('common.transactions') }}</span>
        <card-tooltip v-if="isPendingBrowsingPermission" :width="200">
          <span slot="trigger" :data-browsing-permission="browsingPermissionAttribute">
            {{ displayBrowsingPermission }}
          </span>
          {{ displayWaitingTime }}
        </card-tooltip>
        <span v-else :data-browsing-permission="browsingPermissionAttribute">
          {{ displayBrowsingPermission }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmount } from 'lib/utils/amount';

import ProjectLogo from 'molecules/project/ProjectLogo';
import ProjectControlIcons from './ProjectControlIcons';
import CardTooltip from './CardTooltip';

import { PENDING as PENDING_STATUS } from '../const';

// eslint-disable-next-line no-useless-escape
const PROTOCOL_REGEX = /^(http(s?))\:\/\//i;

export default {
  name: 'PartnerProjectCard',
  components: {
    ProjectLogo,
    ProjectControlIcons,
    CardTooltip,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isInactive() {
      return this.project.isInactive;
    },
    formattedLink() {
      return this.project?.link?.replace(PROTOCOL_REGEX, '') ?? '';
    },
    transactionsBrowsingPermission() {
      return this.project.transactionsBrowsingPermission;
    },
    transactionBrowsingRequestStatus() {
      return this.project.transactionBrowsingRequestStatus;
    },
    transactionBrowsingRequestMinutesToExpire() {
      return this.project.transactionBrowsingRequestMinutesToExpire;
    },
    isPendingBrowsingPermission() {
      return this.transactionBrowsingRequestStatus === PENDING_STATUS;
    },
    browsingPermissionAttribute() {
      if (this.isPendingBrowsingPermission) {
        return 'pending';
      }

      if (!this.transactionsBrowsingPermission) {
        return 'disabled';
      }

      return 'enabled';
    },
    displayBrowsingPermission() {
      if (this.isPendingBrowsingPermission) {
        return this.$t('request.sent');
      }

      return this.transactionsBrowsingPermission ? this.$t('access.opened') : this.$t('get.access');
    },
    formattedRequestMinutesToExpire() {
      const hours = this.transactionBrowsingRequestMinutesToExpire / 60;
      const rhours = Math.floor(hours);
      const minutes = (hours - rhours) * 60;
      const rminutes = Math.round(minutes);

      if (rhours) {
        return `${rhours} ${this.$t('common.hour')} ${rminutes} ${this.$t('common.minute')}`;
      }

      return `${rminutes} ${this.$t('common.minute')}`;
    },
    displayWaitingTime() {
      return this.$t('resending.possible.through', { displayTime: this.formattedRequestMinutesToExpire });
    },
    displayBalance() {
      return formatAmount(this.project.balance, this.project.currency);
    },
    displayAvgAmount() {
      return formatAmount(this.project.avgAmount, this.project.currency);
    },
  },
};
</script>

<style lang="sass" scoped>
  @import './project-card.sass'

  .project-info
    font-size: 12px
    padding: 30px 20px 20px
    border-radius: 0 0 5px 5px

    span
      color: #fff

    span[data-browsing-permission=disabled]
      color: #9B51E0

    span[data-browsing-permission=pending]
      background: linear-gradient(249.76deg, rgba(235, 0, 27, 0.7) -8.32%, rgba(247, 158, 27, 0.7) 108.85%), rgba(255, 255, 255, 0.5)
      -webkit-background-clip: text
      -webkit-text-fill-color: transparent

  .project-info-price
    font-size: 14px
</style>
